import React, { useState } from "react";
import { readableTextColour } from "../../utils/colourContrast";
import { FinanceUserField } from "../../utils/finance";

const DEFAULT_THEME_COLOUR = "#004AAE";
// Minimum number of digits we accept as a plausible phone number.
const MIN_PHONE_DIGITS = 7;

export interface FinanceDetailsValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

// Field metadata: label + input type. Order also drives the rendered order.
const FIELD_META: Record<
  FinanceUserField,
  { label: string; type: string; autoComplete: string }
> = {
  firstName: { label: "First Name", type: "text", autoComplete: "given-name" },
  lastName: { label: "Last Name", type: "text", autoComplete: "family-name" },
  email: { label: "Email", type: "email", autoComplete: "email" },
  phone: { label: "Phone", type: "tel", autoComplete: "tel" },
};

const FIELD_ORDER: FinanceUserField[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
];

interface FinanceDetailsModalProps {
  missingFields: FinanceUserField[];
  initialValues: FinanceDetailsValues;
  onSubmit: (values: FinanceDetailsValues) => void;
  onClose: () => void;
  saving: boolean;
  errorMessage?: string;
  themeColour1?: string;
  themeColour2?: string;
}

const countDigits = (value: string): number => (value.match(/\d/g) || []).length;

// Modal that captures whichever required finance details are missing on the user's
// profile (phone, and name/email if those are blank too), so the parent can save them
// and re-run the finance application. Presentational + props-driven, mirroring
// NewBuildReserveModal; the async save/re-run is owned by the parent.
const FinanceDetailsModal: React.FC<FinanceDetailsModalProps> = ({
  missingFields,
  initialValues,
  onSubmit,
  onClose,
  saving,
  errorMessage,
  themeColour1,
  themeColour2,
}) => {
  const [values, setValues] = useState<FinanceDetailsValues>(initialValues);

  const headerColour = themeColour1 || DEFAULT_THEME_COLOUR;
  const headerTextColour = readableTextColour(headerColour);
  const submitColour = themeColour2 || DEFAULT_THEME_COLOUR;

  const orderedMissing = FIELD_ORDER.filter((field) =>
    missingFields.includes(field)
  );

  const isFieldValid = (field: FinanceUserField): boolean => {
    const value = values[field].trim();
    if (value === "") return false;
    if (field === "phone") return countDigits(value) >= MIN_PHONE_DIGITS;
    return true;
  };

  const allValid = orderedMissing.every(isFieldValid);

  const handleChange = (field: FinanceUserField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!allValid || saving) return;
    onSubmit({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
    });
  };

  return (
    <div
      className="fixed inset-0 z-[999999] overflow-y-auto flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.65)" }}
    >
      <div className="flex flex-col bg-white rounded-xl shadow-xl w-11/12 max-w-md overflow-hidden">
        {/* Header */}
        <div
          className="flex justify-between items-center py-3 px-4"
          style={{ background: headerColour }}
        >
          <h3 className="font-bold text-lg" style={{ color: headerTextColour }}>
            A few more details
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="transition-colors duration-200"
            style={{ color: headerTextColour }}
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form
          data-testid="finance-details-form"
          onSubmit={handleSubmit}
          className="p-4 flex flex-col gap-4"
        >
          <p className="text-sm text-gray-600">
            We just need a little more information to submit your finance
            application.
          </p>

          {orderedMissing.map((field) => {
            const meta = FIELD_META[field];
            const inputId = `finance-detail-${field}`;
            return (
              <div key={field} className="flex flex-col gap-1">
                <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
                  {meta.label}
                </label>
                <input
                  id={inputId}
                  type={meta.type}
                  autoComplete={meta.autoComplete}
                  value={values[field]}
                  disabled={saving}
                  onChange={(e) => handleChange(field, e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-100"
                  style={{ outlineColor: submitColour }}
                />
              </div>
            );
          })}

          {errorMessage && (
            <p className="text-sm text-red-600">{errorMessage}</p>
          )}

          <button
            type="submit"
            disabled={!allValid || saving}
            className="py-3 px-4 rounded-md font-medium shadow-sm transition-all text-sm"
            style={{
              background: !allValid || saving ? "grey" : submitColour,
              color: "#f2f9fa",
              cursor: !allValid || saving ? "not-allowed" : "pointer",
              opacity: !allValid || saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving…" : "Save & Continue"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FinanceDetailsModal;
